var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { Box, InteractiveDiv, LinkButton, Typography } from '@pinwheel/origami';
import React, { useMemo, useState } from 'react';
import CompanyIcon from '../../../components/CompanyIcon';
import { useOpts } from '../../../context';
import { EventAction } from '../../../types/events';
import { useScrolledViewSx } from '../../../utils/useScrolledViewSx';
import { useGetLocalizedString } from '../../../hooks/useGetLocalizedString';
import { useAppSelector } from '../../../state';
import { selectLinkTokenPayload } from '../../../state/slices/slice-link-params';
var ITEMS_PER_ROW = 3;
var GridItem = function (_a) {
    var platform = _a.platform, onSelect = _a.onSelect;
    // Apply an accessible name to the button container. Since the label (platform name)
    // is already present on the DOM, use `aria-labelledby` instead of `aria-label`.
    var labelId = "searchGridItem-platform-".concat(platform.id);
    return (React.createElement(InteractiveDiv, { key: platform.id, onClick: function () { return onSelect(platform); }, "aria-labelledby": labelId, style: {
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            flex: 1,
        } },
        React.createElement(CompanyIcon, { result: platform, size: "lg" }),
        React.createElement(Typography, { variant: "caption", align: "center", id: labelId, as: "div", sx: {
                textAlign: 'center',
                marginTop: 0.5,
            } }, platform.name)));
};
var DivSpacer = function () { return React.createElement("div", { style: { flex: 1 }, role: "presentation" }); };
var SearchTabGrid = function (_a) {
    var tab = _a.tab, providers = _a.providers, onSelect = _a.onSelect, onCantFind = _a.onCantFind;
    var _b = useState(false), isScrolled = _b[0], setIsScrolled = _b[1];
    var scrolledViewSx = useScrolledViewSx({ isScrolled: isScrolled });
    var getLocalizedString = useGetLocalizedString();
    var opts = useOpts();
    var fireProperEventType = opts.fireProperEventType;
    var linkTokenPayload = useAppSelector(selectLinkTokenPayload);
    var isTaxPlatform = linkTokenPayload.platformType === 'tax';
    var elements = useMemo(function () {
        var arrays = [];
        // Group providers into arrays of ITEMS_PER_ROW and place provider in a GridItem
        for (var i = 0; i < (providers === null || providers === void 0 ? void 0 : providers.length); i += ITEMS_PER_ROW) {
            var rowItems = providers
                .slice(i, i + ITEMS_PER_ROW)
                .map(function (x) { return React.createElement(GridItem, { platform: x, key: x.id, onSelect: onSelect }); });
            arrays.push(rowItems);
        }
        // Pad last row with spacer elements to align left
        var lastRow = arrays[arrays.length - 1];
        while ((lastRow === null || lastRow === void 0 ? void 0 : lastRow.length) < ITEMS_PER_ROW) {
            lastRow.push(React.createElement(DivSpacer, { key: lastRow.length }));
        }
        // Wrap each row with a row element
        return arrays.map(function (row, i) { return (React.createElement(Box, { key: i, sx: {
                display: 'flex',
                flexDirection: 'row',
                padding: '0 24px 8px',
                marginTop: '16px',
                flexShrink: 0,
            } }, row)); });
    }, [onSelect, providers]);
    return (React.createElement(Box, { "data-testid": "SearchTabGrid", onScroll: function (e) {
            if (e.currentTarget.scrollTop > 0 && !isScrolled) {
                fireProperEventType(EventAction.SCROLL, opts, { tab: tab });
                setIsScrolled(true);
            }
        }, role: "tabpanel", sx: __assign({ display: 'flex', flexDirection: 'column', overflowY: 'auto', flex: 1 }, scrolledViewSx) },
        elements,
        !isTaxPlatform && (React.createElement("div", { style: { margin: '24px 16px 88px 16px' } },
            React.createElement(Typography, { color: "light", variant: "label", align: "center", sx: { marginBottom: '12px' } }, getLocalizedString('CANT_FIND_RESULT')),
            React.createElement(Typography, { variant: "caption", align: "center", as: "div" },
                React.createElement(LinkButton, { "data-testid": "searchTabGrid-cantFindItPrompt", onClick: function () { return onCantFind(); } }, getLocalizedString('SEARCH_ALL_PROVIDERS')))))));
};
export default SearchTabGrid;
